import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Mail,
  MessageCircle,
  BookOpen,
  LifeBuoy,
  ExternalLink,
  Search,
} from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';

const faqItems = [
  {
    q: 'How do I approve a visitor?',
    a: 'Go to Visitors in the sidebar, find the pending visitor request, and click Approve. The visitor will receive an OTP or QR code for entry at the gate.',
    roles: ['ALL'],
  },
  {
    q: 'How do I track a package delivery?',
    a: 'Navigate to Deliveries. Packages logged at the gate will appear here with their status. You will be notified when a delivery is received.',
    roles: ['ALL'],
  },
  {
    q: 'How do I post a notice or event?',
    a: 'Go to Noticeboard or Events. Click "New Notice" or "Create Event", fill in the details, and submit. Society admins can manage posting permissions under Community Features.',
    roles: ['ALL'],
  },
  {
    q: 'Why can\'t I access Billing or Residents pages?',
    a: 'These pages are restricted to Society Admins and Staff. If you need access, contact your Society Admin to update your role.',
    roles: ['RESIDENT'],
  },
  {
    q: 'How do I invite new residents to Nexora?',
    a: 'Go to the Admin panel → Invite Management. Generate an invite link or approve access requests submitted by residents.',
    roles: ['SOCIETY_ADMIN', 'STAFF'],
  },
  {
    q: 'How do I enable/disable community features for residents?',
    a: 'Go to Settings → Community Features. Toggle Noticeboard posting and Events creation on or off. Only the Society Admin can change these settings.',
    roles: ['SOCIETY_ADMIN'],
  },
  {
    q: 'How do I change my password?',
    a: 'Go to Settings → Change Password. Enter your current password and set a new one. The change takes effect immediately.',
    roles: ['ALL'],
  },
  {
    q: 'I forgot my password — what do I do?',
    a: 'On the login page, click "Forgot password?" and enter your email. You will receive a reset link within a few minutes. Check your spam folder if it doesn\'t arrive.',
    roles: ['ALL'],
  },
  {
    q: 'How do I raise a maintenance request?',
    a: 'Go to Operations → Maintenance. Click "New Request", describe the issue, and select the category. Staff will be notified and can update the ticket status.',
    roles: ['ALL'],
  },
  {
    q: 'How do I switch between Premium Themes?',
    a: 'In the sidebar under Premium Themes, click on Executive Suite, Modern Minimal, or Corporate Edge to preview each theme. Contact your Society Admin to set one as the default.',
    roles: ['SOCIETY_ADMIN', 'STAFF'],
  },
];

const FaqItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: '1px solid var(--nx-border)',
      borderRadius: 10,
      overflow: 'hidden',
      transition: 'border-color 200ms',
    }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '14px 18px',
          background: open ? 'var(--nx-surface-3)' : 'var(--nx-surface-2)',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'var(--font-body)',
          transition: 'background 150ms',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text-primary)', lineHeight: 1.4 }}>{q}</span>
        <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </span>
      </button>
      {open && (
        <div style={{
          padding: '12px 18px 16px',
          fontSize: 13,
          color: 'var(--nx-text-secondary)',
          lineHeight: 1.6,
          background: 'var(--nx-surface-2)',
          borderTop: '1px solid var(--nx-border)',
        }}>
          {a}
        </div>
      )}
    </div>
  );
};

const HelpPage: React.FC = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const visibleFaqs = faqItems.filter(item =>
    (item.roles.includes('ALL') || item.roles.includes(user?.role ?? '')) &&
    (search === '' || item.q.toLowerCase().includes(search.toLowerCase()) || item.a.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <PageLayout>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--nx-text-primary)',
          letterSpacing: '-0.03em',
          marginBottom: 6,
        }}>
          Help & Support
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          Find answers, contact support, and learn how to get the most out of Nexora.
        </p>
      </div>

      {/* Contact cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          {
            icon: <Mail size={20} />,
            color: '#1A6CF6',
            title: 'Email Support',
            desc: 'Reach our team via email. Responses within 24 hours on business days.',
            action: 'admin@pentagonanalytics.io',
            href: 'mailto:admin@pentagonanalytics.io',
          },
          {
            icon: <MessageCircle size={20} />,
            color: '#10B981',
            title: 'WhatsApp Chat',
            desc: 'Chat with a support agent directly. Available Mon–Sat, 9 AM – 7 PM.',
            action: 'Open WhatsApp',
            href: 'https://wa.me/919999999999',
          },
          {
            icon: <BookOpen size={20} />,
            color: '#7C3AED',
            title: 'Documentation',
            desc: 'Step-by-step guides, feature walkthroughs, and admin reference docs.',
            action: 'View Docs',
            href: '/dashboard/docs',
          },
        ].map(card => (
          <div key={card.title} style={{
            background: 'var(--nx-surface-2)',
            border: '1px solid var(--nx-border)',
            borderRadius: 12,
            padding: '20px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: `${card.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: card.color,
            }}>
              {card.icon}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 4, fontFamily: 'Syne, sans-serif' }}>
                {card.title}
              </div>
              <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5, marginBottom: 10 }}>
                {card.desc}
              </p>
              <a
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: card.color,
                  textDecoration: 'none',
                }}
              >
                {card.action} <ExternalLink size={11} />
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* System status */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 16px',
        borderRadius: 10,
        background: 'rgba(16,185,129,0.06)',
        border: '1px solid rgba(16,185,129,0.2)',
        marginBottom: 28,
      }}>
        <LifeBuoy size={15} style={{ color: '#10B981', flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: 'var(--nx-text-secondary)' }}>
          All Nexora systems are <strong style={{ color: '#10B981' }}>operational</strong>. No incidents reported.
        </span>
      </div>

      {/* FAQ */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 3 }}>
              Frequently Asked Questions
            </h2>
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Showing questions relevant to your role</p>
          </div>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--nx-text-muted)' }} />
            <input
              type="text"
              placeholder="Search FAQs…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '7px 12px 7px 30px',
                borderRadius: 8,
                border: '1px solid var(--nx-border)',
                background: 'var(--nx-surface-2)',
                color: 'var(--nx-text-primary)',
                fontSize: 12,
                outline: 'none',
                width: 200,
                fontFamily: 'var(--font-body)',
              }}
            />
          </div>
        </div>

        {visibleFaqs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>
            No results for "{search}"
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleFaqs.map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default HelpPage;
